import { useState, useRef, useEffect } from 'react';
import { Camera, AlertCircle, CheckCircle, Loader, Lock, Calendar } from 'lucide-react';
import { useUpload } from '../../hooks/useUpload.js';
import { useAuth } from '../../hooks/useAuth.js';

// Removed API_BASE_URL as it is now handled by the service

const UserProfile = ({ userData, onUserUpdate }) => {
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const avatarInputRef = useRef(null);
  const { uploadSingleFile, uploading: uploadingImage } = useUpload();
  const { handleUpdateProfile } = useAuth();

  const [profileData, setProfileData] = useState({
    firstName: userData?.firstName || '',
    lastName: userData?.lastName || '',
    contact: userData?.contactNumber || userData?.landline || userData?.contact || '',
    email: userData?.email || '',
    gender: userData?.gender || '',
    dateOfBirth: userData?.dateOfBirth || '',
    avatar: userData?.avatar || '',
  });

  const [editData, setEditData] = useState({
    firstName: userData?.firstName || '',
    lastName: userData?.lastName || '',
    email: userData?.email || '',
    gender: userData?.gender || '',
    dateOfBirth: userData?.dateOfBirth || '',
    avatar: userData?.avatar || '',
  });

  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (userData) {
      setProfileData({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        contact: userData.contactNumber || userData.landline || userData.contact || '',
        email: userData.email || '',
        gender: userData.gender || '',
        dateOfBirth: userData.dateOfBirth || '',
        avatar: userData.avatar || '',
      });
      setEditData({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        gender: userData.gender || '',
        dateOfBirth: userData.dateOfBirth || '',
        avatar: userData.avatar || '',
      });
      setHasChanges(false);
    }
  }, [userData]);

  // Check if data has changed
  useEffect(() => {
    const changed =
      editData.firstName !== profileData.firstName ||
      editData.lastName !== profileData.lastName ||
      editData.email !== profileData.email ||
      editData.gender !== profileData.gender ||
      editData.dateOfBirth !== profileData.dateOfBirth ||
      editData.avatar !== profileData.avatar;

    setHasChanges(changed);
  }, [editData, profileData]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5000000) {
      setError('Image file is too large (max 5MB)');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    setError(null);
    try {
      const uploadedUrl = await uploadSingleFile(file);

      // Ensure we have a valid URL string
      if (!uploadedUrl || typeof uploadedUrl !== 'string') {
        console.error('Invalid URL returned:', uploadedUrl);
        throw new Error(`Invalid upload response - received: ${JSON.stringify(uploadedUrl)}`);
      }

      setEditData(prev => ({
        ...prev,
        avatar: uploadedUrl,
      }));
      setSuccess('Image uploaded successfully');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload image');
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setError(null);

    try {
      const payload = {
        firstName: editData.firstName,
        lastName: editData.lastName,
        email: editData.email,
        gender: editData.gender,
        dateOfBirth: editData.dateOfBirth,
      };

      if (editData.avatar && editData.avatar !== profileData.avatar) {
        payload.avatar = editData.avatar;
      }

      await handleUpdateProfile(payload);

      setSuccess('Profile updated successfully!');
      setProfileData(editData);
      onUserUpdate?.();

      setTimeout(() => {
        setSuccess(null);
      }, 2000);
    } catch (err) {
      console.error('Update error:', err);
      setError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="w-full">
      {/* Messages */}
      <div className="mb-4 sm:mb-6">
        {error && (
          <div className="bg-red-50/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3 mb-4">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-red-300 text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="bg-green-50/10 border border-green-500/30 rounded-xl p-4 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
            <p className="text-green-300 text-sm">{success}</p>
          </div>
        )}
      </div>

      {/* Profile Card Section - Centered */}
      <div className="mb-4 sm:mb-6">
        <div className="bg-gradient-to-br from-gray-700/40 to-gray-800/40 rounded-xl sm:rounded-2xl border-2 border-gray-600/50 p-4 sm:p-6 lg:p-8 backdrop-blur-xl shadow-2xl">
          <div className="flex flex-col items-center">
            {/* Avatar with Edit Button */}
            <div className="relative mb-4">
              <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gradient-to-br from-green-400 to-green-500 flex items-center justify-center overflow-hidden border-4 border-green-400 shadow-2xl">
                {editData.avatar ? (
                  <img src={editData.avatar} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-3xl sm:text-4xl font-bold text-white">
                    {editData.firstName?.charAt(0)}{editData.lastName?.charAt(0)}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => avatarInputRef.current?.click()}
                className="absolute bottom-0 right-0 bg-green-500 hover:bg-green-600 text-white p-2 sm:p-3 rounded-full transition-all shadow-lg hover:scale-110 transform"
                disabled={uploadingImage}
              >
                {uploadingImage ? (
                  <Loader className="w-5 h-5 animate-spin" />
                ) : (
                  <Camera className="w-5 h-5" />
                )}
              </button>
            </div>

            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />

            {/* Name */}
            <h2 className="text-xl sm:text-2xl font-bold text-white text-center">
              {editData.firstName} {editData.lastName}
            </h2>

            {/* Update Photo Link */}
            <button
              onClick={() => avatarInputRef.current?.click()}
              className="mt-3 text-green-400 hover:text-green-300 font-semibold text-sm transition-colors"
            >
              Update your photo
            </button>
          </div>
        </div>
      </div>

      {/* Form Section Card */}
      <div className="pb-6 sm:pb-8 lg:pb-12">
        <form onSubmit={handleSaveProfile}>
          <div className="bg-gradient-to-br from-gray-700/40 to-gray-800/40 rounded-xl sm:rounded-2xl border-2 border-gray-600/50 p-4 sm:p-6 backdrop-blur-xl shadow-2xl">
            {/* 2-Column Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
              {/* Row 1: First Name & Last Name */}
              <div>
                <label className="block text-gray-300 font-semibold mb-1 sm:mb-2 text-sm sm:text-base">First Name</label>
                <input
                  type="text"
                  value={editData.firstName}
                  onChange={(e) => setEditData(prev => ({ ...prev, firstName: e.target.value }))}
                  className="w-full px-3 py-2 sm:px-4 sm:py-2 bg-white/10 border border-white/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-400/20 backdrop-blur-sm transition-all text-sm sm:text-base"
                />
              </div>

              <div>
                <label className="block text-gray-300 font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Last Name</label>
                <input
                  type="text"
                  value={editData.lastName}
                  onChange={(e) => setEditData(prev => ({ ...prev, lastName: e.target.value }))}
                  className="w-full px-3 py-2 sm:px-4 sm:py-2 bg-white/10 border border-white/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-400/20 backdrop-blur-sm transition-all text-sm sm:text-base"
                />
              </div>

              {/* Row 2: Contact & Email */}
              <div>
                <label className="block text-gray-300 font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Contact</label>
                <div className="relative">
                  <Lock className="absolute left-3 sm:left-4 top-2 w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                  <input
                    type="tel"
                    value={profileData.contact}
                    disabled
                    className="w-full pl-9 sm:pl-12 pr-3 sm:pr-4 py-2 bg-white/5 border border-white/20 rounded-lg text-gray-400 focus:outline-none cursor-not-allowed text-sm sm:text-base"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-300 font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Email Address</label>
                <input
                  type="email"
                  value={editData.email}
                  onChange={(e) => setEditData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 sm:px-4 sm:py-2 bg-white/10 border border-white/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-400/20 backdrop-blur-sm transition-all text-sm sm:text-base"
                />
              </div>

              {/* Row 3: Gender & Date of Birth */}
              <div>
                <label className="block text-gray-300 font-semibold mb-2 sm:mb-3 text-sm sm:text-base">Gender</label>
                <select
                  value={editData.gender}
                  onChange={(e) => setEditData(prev => ({ ...prev, gender: e.target.value }))}
                  className="w-full px-3 py-2.5 sm:px-4 sm:py-3 bg-white/10 border border-white/30 rounded-lg text-white focus:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-400/20 backdrop-blur-sm transition-all text-sm sm:text-base"
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-300 font-semibold mb-2 sm:mb-3 text-sm sm:text-base">Date of Birth</label>
                <div className="relative">
                  <input
                    type="date"
                    value={editData.dateOfBirth}
                    onChange={(e) => setEditData(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                    className="w-full px-3 py-2.5 sm:px-4 sm:py-3 bg-white/10 border border-white/30 rounded-lg text-white focus:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-400/20 backdrop-blur-sm transition-all text-sm sm:text-base"
                  />
                  <Calendar className="absolute right-3 sm:right-4 top-2.5 sm:top-3.5 w-4 h-4 sm:w-5 sm:h-5 text-gray-500 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex gap-2 sm:gap-4">
              <button
                type="submit"
                disabled={updating || !hasChanges}
                className={`flex-1 font-bold py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 text-sm sm:text-base ${hasChanges
                    ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 text-white shadow-lg hover:shadow-green-500/50'
                    : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  }`}
              >
                {updating && <Loader className="w-4 h-4 animate-spin" />}
                Save Changes
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserProfile;
